import { Phases } from "../../misc/api/ApiPhases";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { SessionScheduleItem, VisitScheduleItem, DeviceTypes } from "../../misc/api/apiTypes";
import { Device } from "../../misc/api/DeviceLinkApi/Devices";
import { StudyScheduleSession, Device as WpDevice, StudyScheduleVisit } from "webportalclient";

export enum UpdatingDeviceLinkEnum {
    NotUpdating = "Not Updating",
    Updating = "Updating",
    FailedUpdate = "Failed Update"
}

export enum SubjectLocation {
    Remote = 'SL_REMOTE',
    Local = 'SL_LOCAL'
}

export enum StartSessionWizardActionTypes {
    SetSessionType = "SetSessionType",
    SetSessionCandidates = "SetSessionCandidates",
    SetSession = "SetSession",
    SubjectLocation = "SubjectLocation",
    SetOutstandingVisits = "SetOutstandingVisits",
    SetCurrentVisitItems = "SetCurrentVisitItems",
    SetFutureVisitItems = "SetFutureVisitItems",
    SetSkippingAnnotation = "SetSkippingAnnotation",
    SetSelectedDeviceType = "SetSelectedDeviceType",
    SetSelectedDevice = "SetSelectedDevice",
    RaiseConfigurationWarning = "RaiseConfigurationWarning",
    SetVitaloJak1DeviceSerial = "SetVitaloJak1DeviceSerial",
    SetVitaloJak1MediaSerial = "SetVitaloJak1MediaSerial",
    SetVitaloJak1IsNotConfigured = "SetVitaloJak1IsNotConfigured",
    SetRollingVisitItems = "SetRollingVisitItems",
    RemoteDeviceSelect = "RemoteDeviceSelect",
    ResetSelectedDevice = "ResetSelectedDevice",
}

export interface KeyedVisitSessionItems {
    key: string;
    phase: Phases;
    value: StudyScheduleSession[];
}

interface VitaloJak1State {
    deviceSerial: string;
    mediaSerial: string;
    notConfigured: boolean;
}

interface DiaryState {
    remoteDevice: WpDevice;
}

export interface StartSessionWizardReducerState {
    selectedSessionType: SessionTypes;
    supportMultiSelect: boolean;
    outstandingVisits: StudyScheduleVisit[];
    currentVisitItems: StudyScheduleSession[];
    futureVisitItems: KeyedVisitSessionItems[];
    rollingVisitItems: StudyScheduleSession[];
    selectedSessionScheduleItem: Array<{ session: string; checked: boolean }>;
    subjectLocation: SubjectLocation;
    skippingAnnotation: string;
    selectedDeviceType: DeviceTypes;
    selectedDevice: Device;
    configurationWarning: string;
    vitalojak1: VitaloJak1State;
    diary: DiaryState;
}

export const initialState: StartSessionWizardReducerState = {
    selectedSessionType: null,
    supportMultiSelect: false,
    outstandingVisits: [],
    currentVisitItems: [],
    futureVisitItems: [],
    rollingVisitItems: [],
    selectedSessionScheduleItem: [],
    subjectLocation: null,
    skippingAnnotation: null,
    selectedDeviceType: null,
    selectedDevice: null,
    configurationWarning: null,
    vitalojak1: {
        deviceSerial: "",
        mediaSerial: "",
        notConfigured: false,
    },
    diary: {
        remoteDevice: null
    }
}

export class SetSessionTypeAction {
    type: StartSessionWizardActionTypes.SetSessionType;
    sessionType: SessionTypes;
    multiSelectEnabled: boolean;

    static create(sessionType: SessionTypes, multiSelectEnabled: boolean): SetSessionTypeAction {
        return {
            type: StartSessionWizardActionTypes.SetSessionType,
            sessionType: sessionType,
            multiSelectEnabled: multiSelectEnabled,
        };
    }
}

export class SetSessionCandidatesAction {
    type: StartSessionWizardActionTypes.SetSessionCandidates;
    sessionIds: Array<string>;

    static create(sessionIds: Array<string>): SetSessionCandidatesAction {
        return {
            type: StartSessionWizardActionTypes.SetSessionCandidates,
            sessionIds: sessionIds,
        };
    }
}

export class SetSessionAction {
    type: StartSessionWizardActionTypes.SetSession;
    session: string;
    checked: boolean;

    static create(session: string, checked: boolean): SetSessionAction {
        return {
            type: StartSessionWizardActionTypes.SetSession,
            session: session,
            checked: checked,
        };
    }
}

export class SubjectLocationAction {
    type: StartSessionWizardActionTypes.SubjectLocation;
    location: SubjectLocation;

    static create(location: SubjectLocation): SubjectLocationAction {
        return {
            type: StartSessionWizardActionTypes.SubjectLocation,
            location: location,
        };
    }
}

export class SetOustandingVisitsAction {
    type: StartSessionWizardActionTypes.SetOutstandingVisits;
    visits: StudyScheduleVisit[];

    static create(visits: StudyScheduleVisit[]): SetOustandingVisitsAction {
        return {
            type: StartSessionWizardActionTypes.SetOutstandingVisits,
            visits: visits,
        }
    }

}

export class SetCurrentVisitItemsAction {
    type: StartSessionWizardActionTypes.SetCurrentVisitItems;
    currentVisitItems: StudyScheduleSession[];

    static create(currentVisitItems: StudyScheduleSession[]): SetCurrentVisitItemsAction {
        return {
            type: StartSessionWizardActionTypes.SetCurrentVisitItems,
            currentVisitItems: currentVisitItems,
        }
    }
}

export class SetFutureVisitItemsAction {
    type: StartSessionWizardActionTypes.SetFutureVisitItems;
    futureVisitItems: KeyedVisitSessionItems[];

    static create(futureVisitItems: KeyedVisitSessionItems[]): SetFutureVisitItemsAction {
        return {
            type: StartSessionWizardActionTypes.SetFutureVisitItems,
            futureVisitItems: futureVisitItems,
        }
    }
}

export class SetRollingVisitItemsAction {
    type: StartSessionWizardActionTypes.SetRollingVisitItems;
    rollingVisitItems: StudyScheduleSession[];

    static create(rollingVisitItems: StudyScheduleSession[]): SetRollingVisitItemsAction {
        return {
            type: StartSessionWizardActionTypes.SetRollingVisitItems,
            rollingVisitItems: rollingVisitItems,
        }
    }
}

export class SetSkippingAnnotationAction {
    type: StartSessionWizardActionTypes.SetSkippingAnnotation;
    annotation: string;

    static create(annotation: string): SetSkippingAnnotationAction {
        return {
            type: StartSessionWizardActionTypes.SetSkippingAnnotation,
            annotation: annotation,
        }
    }
}

export class SetSelectedDeviceTypeAction {
    type: StartSessionWizardActionTypes.SetSelectedDeviceType;
    deviceType: DeviceTypes;

    static create(deviceType: DeviceTypes): SetSelectedDeviceTypeAction {
        return {
            type: StartSessionWizardActionTypes.SetSelectedDeviceType,
            deviceType: deviceType,
        };
    }
}

export class SetSelectedDeviceAction {
    type: StartSessionWizardActionTypes.SetSelectedDevice;
    device: Device;

    static create(device: Device): SetSelectedDeviceAction {
        return {
            type: StartSessionWizardActionTypes.SetSelectedDevice,
            device: device,
        };
    }
}

export class SetVitaloJak1DeviceSerialAction {
    type: StartSessionWizardActionTypes.SetVitaloJak1DeviceSerial;
    deviceSerial: string;

    static create(deviceSerial: string): SetVitaloJak1DeviceSerialAction {
        return {
            type: StartSessionWizardActionTypes.SetVitaloJak1DeviceSerial,
            deviceSerial: deviceSerial,
        };
    }
}

export class SetVitaloJak1MediaSerialAction {
    type: StartSessionWizardActionTypes.SetVitaloJak1MediaSerial;
    mediaSerial: string;

    static create(mediaSerial: string): SetVitaloJak1MediaSerialAction {
        return {
            type: StartSessionWizardActionTypes.SetVitaloJak1MediaSerial,
            mediaSerial: mediaSerial,
        };
    }
}

export class SetVitaloJak1IsNotConfiguredAction {
    type: StartSessionWizardActionTypes.SetVitaloJak1IsNotConfigured;
    notConfigured: boolean;

    static create(state: boolean): SetVitaloJak1IsNotConfiguredAction {
        return {
            type: StartSessionWizardActionTypes.SetVitaloJak1IsNotConfigured,
            notConfigured: state
        }
    }
}

export class RaiseConfigurationWarningAction {
    type: StartSessionWizardActionTypes.RaiseConfigurationWarning;
    warningMessage: string;

    static create(warningMessage: string): RaiseConfigurationWarningAction {
        return {
            type: StartSessionWizardActionTypes.RaiseConfigurationWarning,
            warningMessage: warningMessage
        }
    }
}

export class RemoteDeviceSelectAction {
    type: StartSessionWizardActionTypes.RemoteDeviceSelect;
    device: WpDevice;

    static create(device: WpDevice): RemoteDeviceSelectAction {
        return {
            type: StartSessionWizardActionTypes.RemoteDeviceSelect,
            device: device,
        };
    }
}

export class ResetSelectedDeviceAction {
    type: StartSessionWizardActionTypes.ResetSelectedDevice;

    static create(): ResetSelectedDeviceAction {
        return {
            type: StartSessionWizardActionTypes.ResetSelectedDevice,
        };
    }
}


export type StartSessionWizardActions = SetSessionTypeAction | SetSessionCandidatesAction | SetSessionAction | SubjectLocationAction | SetOustandingVisitsAction | SetCurrentVisitItemsAction | SetFutureVisitItemsAction
    | SetSkippingAnnotationAction | SetSelectedDeviceTypeAction | SetSelectedDeviceAction | SetVitaloJak1DeviceSerialAction | SetVitaloJak1MediaSerialAction | SetVitaloJak1IsNotConfiguredAction | RaiseConfigurationWarningAction
    | SetRollingVisitItemsAction | RemoteDeviceSelectAction | ResetSelectedDeviceAction;

export const startSessionWizardReducer = (state: StartSessionWizardReducerState, action: StartSessionWizardActions): StartSessionWizardReducerState => {
    const handleSetSessionType = (state: StartSessionWizardReducerState, action: SetSessionTypeAction): StartSessionWizardReducerState => {
        return {
            ...state,
            supportMultiSelect: action.multiSelectEnabled,
            selectedSessionType: action.sessionType,
        }
    }

    const handleSetSessionCandidates = (state: StartSessionWizardReducerState, action: SetSessionCandidatesAction): StartSessionWizardReducerState => {
        return {
            ...state,
            selectedSessionScheduleItem: action.sessionIds.map(s =>
                ({
                    session: s,
                    checked: false
                })),
        }
    }

    const handleSetSession = (state: StartSessionWizardReducerState, action: SetSessionAction): StartSessionWizardReducerState => {
        const getSessionItem = () => {
            return state.currentVisitItems.find(cvi => cvi.id === action.session)
                ?? state.futureVisitItems.map(fvi => fvi.value.find(v => v.id === action.session)).filter(v => v)[0];
        }

        const diarySelect = (): StartSessionWizardReducerState => {
            const sessionScheduleItem = state.currentVisitItems.find(cvi => cvi.id === action.session)
                ?? state.rollingVisitItems.find(rvi => rvi.id === action.session);

            let sessionsInVisit = [];
            if (sessionScheduleItem && sessionScheduleItem.rolling) {
                sessionsInVisit = state.rollingVisitItems.filter(rvi => rvi.studyScheduleVisitId === sessionScheduleItem.studyScheduleVisitId).map(s => s.id);
            } else {
                sessionsInVisit = sessionScheduleItem ? state.currentVisitItems.map(s => s.id) : [];
            }

            for (const visit of state.futureVisitItems) {
                const session = visit.value.find(v => v.id === action.session);
                if (session) {
                    sessionsInVisit = visit.value.map(s => s.id)
                    break;
                }
            }

            return {
                ...state,
                selectedSessionScheduleItem: state.selectedSessionScheduleItem.map(ss => {
                    if (sessionsInVisit.includes(ss.session)) {
                        return {
                            ...ss,
                            checked: action.checked,
                        }
                    } else {
                        return {
                            ...ss,
                            checked: false,
                        };
                    }
                })
            }
        }

        const multiSelect = (): StartSessionWizardReducerState => {
            return {
                ...state,
                selectedSessionScheduleItem: state.selectedSessionScheduleItem.map(ss => {
                    if (ss.session === action.session) {
                        return {
                            ...ss,
                            checked: action.checked,
                        }
                    } else {
                        return ss;
                    }
                })
            }
        }

        const singleSelect = (): StartSessionWizardReducerState => {
            return {
                ...state,
                selectedSessionScheduleItem: state.selectedSessionScheduleItem.map(ss => {
                    if (ss.session === action.session) {
                        return {
                            ...ss,
                            checked: action.checked,
                        }
                    } else {
                        return {
                            ...ss,
                            checked: false
                        }
                    }
                })
            }
        }

        switch (state.selectedSessionType) {
            case SessionTypes.EDiary:
                return diarySelect();
            case SessionTypes.Cough:
                const sessionItem = getSessionItem();
                switch (sessionItem.deviceTypeId) {
                    case DeviceTypes.VitaloJAK1:
                        return singleSelect();
                    case DeviceTypes.VitaloJAK2:
                        if (state.supportMultiSelect) {
                            return multiSelect();
                        } else {
                            return singleSelect();
                        }
                    default:
                        multiSelect();
                }
                break;
            default:
                return state.supportMultiSelect ? multiSelect() : singleSelect()
        }
    }

    switch (action.type) {
        case StartSessionWizardActionTypes.SetSessionType:
            return handleSetSessionType(state, action);
        case StartSessionWizardActionTypes.SetSessionCandidates:
            return handleSetSessionCandidates(state, action);
        case StartSessionWizardActionTypes.SetSession:
            return handleSetSession(state, action);
        case StartSessionWizardActionTypes.SubjectLocation:
            return {
                ...state,
                subjectLocation: action.location,
            };
        case StartSessionWizardActionTypes.SetOutstandingVisits:
            return {
                ...state,
                outstandingVisits: action.visits,
            };
        case StartSessionWizardActionTypes.SetCurrentVisitItems:
            return {
                ...state,
                currentVisitItems: action.currentVisitItems,
            };
        case StartSessionWizardActionTypes.SetFutureVisitItems:
            return {
                ...state,
                futureVisitItems: action.futureVisitItems,
            };
        case StartSessionWizardActionTypes.SetSkippingAnnotation:
            return {
                ...state,
                skippingAnnotation: action.annotation,
            };
        case StartSessionWizardActionTypes.SetSelectedDeviceType:
            return {
                ...state,
                selectedDeviceType: action.deviceType,
            };
        case StartSessionWizardActionTypes.SetSelectedDevice:
            return {
                ...state,
                selectedDevice: action.device,
            };
        case StartSessionWizardActionTypes.SetVitaloJak1DeviceSerial:
            return {
                ...state,
                vitalojak1: {
                    ...state.vitalojak1,
                    deviceSerial: action.deviceSerial,
                }
            };
        case StartSessionWizardActionTypes.SetVitaloJak1MediaSerial:
            return {
                ...state,
                vitalojak1: {
                    ...state.vitalojak1,
                    mediaSerial: action.mediaSerial,
                }
            };
        case StartSessionWizardActionTypes.SetVitaloJak1IsNotConfigured:
            return {
                ...state,
                vitalojak1: {
                    ...state.vitalojak1,
                    notConfigured: action.notConfigured,
                }
            };
        case StartSessionWizardActionTypes.RaiseConfigurationWarning:
            return {
                ...state,
                configurationWarning: action.warningMessage,
            };
        case StartSessionWizardActionTypes.SetRollingVisitItems:
            return {
                ...state,
                rollingVisitItems: action.rollingVisitItems,
            };
        case StartSessionWizardActionTypes.RemoteDeviceSelect:
            return {
                ...state,
                diary: {
                    ...state.diary,
                    remoteDevice: action.device,
                },
            };
        case StartSessionWizardActionTypes.ResetSelectedDevice:
            return {
                ...state,
                selectedDevice: null,
            };
    }
}
